import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { FadeUp, MaskedLine } from "../components/motion";
import { ELink } from "../components/Editable";
import { ShareButtons } from "../components/ShareButtons";
import { PhotoLightbox } from "../components/PhotoLightbox";
import { SEO } from "../components/SEO";
import RichText from "../components/RichText";

const IMGS = [
  "/gallery/c.jpg",
  "/gallery/atelier-2.jpg",
  "/gallery/a.jpg",
  "/gallery/atelier-1.jpg",
  "/gallery/b.jpg",
  "/gallery/atelier-3.jpg",
];

export default function Realisations() {
  const { t, ts, site, lang } = useLang();
  // Galerie gérée par l'admin (onglet Réalisations) — repli sur la galerie
  // statique ci-dessus si le service est indisponible ou vide.
  const [dbPhotos, setDbPhotos] = useState(null);
  useEffect(() => {
    api.get("/realisation-photos").then((r) => setDbPhotos(r.data?.photos || [])).catch(() => setDbPhotos([]));
  }, []);
  const managed = !!(dbPhotos && dbPhotos.length);
  const cap = (p) => ((lang === "en" && p.caption_en) ? p.caption_en : p.caption_fr) || "";
  const bannerDb = managed ? dbPhotos.find((p) => p.kind === "banner") : null;
  const banner = {
    src: bannerDb?.url || site("img.real_banner") || "/real-banner.png",
    caption: bannerDb ? cap(bannerDb) : t("realisations.official"),
  };
  const items = managed
    ? dbPhotos.filter((p) => p.kind !== "banner").map((p) => ({ img: p.url, caption: cap(p) }))
    : t("realisations.items").map((item, i) => ({ ...item, img: site(`img.gallery_${i}`) || IMGS[i % IMGS.length] }));
  const photos = [{ src: banner.src, caption: banner.caption },
    ...items.map((item) => ({ src: item.img, caption: item.caption }))];
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <div data-testid="realisations-page" className="pt-32 pb-28">
      <SEO title={ts("seo.realisations_title")} description={ts("seo.realisations_desc")} path="/realisations"
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage",
                  name: ts("seo.realisations_title"), url: `https://mrtrophee.ca/realisations`,
                  inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
                  isPartOf: { "@type": "WebSite", url: "https://mrtrophee.ca" },
                  about: { "@id": "https://mrtrophee.ca/#entreprise" } }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("realisations.kicker")}</p>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight mb-6">
            <MaskedLine key={ts("realisations.title_1")} delay={0.15}>{t("realisations.title_1")}</MaskedLine>
            <MaskedLine key={ts("realisations.title_2")} delay={0.35} className="italic text-gold">{t("realisations.title_2")}</MaskedLine>
          </h1>
          <p className="text-ivory/60 max-w-xl mb-16">{t("realisations.sub")}</p>
          <ShareButtons title={`${ts("realisations.title_1")} ${ts("realisations.title_2")} — Mr Trophée`} label={t("footer.share_page")} />
          <div className="mb-16" />
        </FadeUp>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-8 space-y-8">
          <FadeUp className="break-inside-avoid">
            <div className="group relative card-clip overflow-hidden bg-ivory cursor-zoom-in" data-testid="realisation-officiel"
              onClick={() => setLightboxIdx(0)}>
              <img src={banner.src} alt="Mr Trophée — trophee.ca" data-edit-image="img.real_banner" className="w-full h-72 object-contain p-6" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-5 pt-14">
                <p className="text-sm text-champagne font-serif italic">{banner.caption}</p>
              </div>
            </div>
          </FadeUp>
          {items.map((item, i) => (
            <FadeUp key={i} delay={(i % 3) * 0.1} className="break-inside-avoid">
              <div className="group relative card-clip overflow-hidden bg-surface cursor-zoom-in" data-testid={`realisation-${i}`}
                onClick={() => setLightboxIdx(i + 1)}>
                <motion.img
                  src={item.img} alt={item.caption}
                  data-edit-image={`img.gallery_${i}`}
                  className={`w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500 ${i % 3 === 1 ? "h-96" : "h-72"}`}
                  whileHover={{ scale: 1.06 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-5 pt-14">
                  <p className="text-sm text-champagne font-serif italic"><RichText text={item.caption} path={`realisations.items.${i}.caption`} /></p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="text-center mt-20">
          <ELink
            editPath="link.realisations_cta"
            to="/boutique" data-testid="realisations-cta"
            className="group inline-flex items-center gap-3 bg-gold text-ink font-semibold px-10 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
          >
            {t("realisations.cta")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </ELink>
        </FadeUp>
      </div>
      <PhotoLightbox photos={photos} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onNavigate={setLightboxIdx} />
    </div>
  );
}
